import { supabase } from '../../../services/supabase'
import type {
  AuthenticatorAssuranceLevel,
  MfaEnrollResult,
  MfaStatus,
  MfaTotpFactor,
} from './mfa.types'

function asTotpFactor (value: unknown): MfaTotpFactor | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const row = value as Record<string, unknown>
  const factorType = row.factor_type ?? row.factorType
  if (factorType !== 'totp') return null
  const id = typeof row.id === 'string' ? row.id : ''
  if (!id) return null
  const status = row.status === 'verified' ? 'verified' : 'unverified'
  return {
    id,
    friendlyName: typeof row.friendly_name === 'string' ? row.friendly_name : 'Authenticator',
    status,
  }
}

/** Parseert listFactors-response; `all` bevat ook unverified factors, `totp` alleen verified. */
export function parseTotpFactorsFromListResponse (data: unknown): MfaTotpFactor[] {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return []
  const record = data as Record<string, unknown>
  const allFactors = Array.isArray(record.all) ? record.all : []
  const source = allFactors.length > 0
    ? allFactors
    : (Array.isArray(record.totp) ? record.totp : [])
  return source
    .map(asTotpFactor)
    .filter((factor): factor is MfaTotpFactor => factor !== null)
}

export function normalizeTotpCode (code: string): string {
  const trimmed = code.replace(/\s/g, '')
  if (!/^\d{6}$/.test(trimmed)) {
    throw new Error('Voer een geldige 6-cijferige code in')
  }
  return trimmed
}

export async function getMfaStatus (): Promise<MfaStatus> {
  const [factorsResult, aalResult] = await Promise.all([
    supabase.auth.mfa.listFactors(),
    supabase.auth.mfa.getAuthenticatorAssuranceLevel(),
  ])

  if (factorsResult.error) throw factorsResult.error
  if (aalResult.error) throw aalResult.error

  const factors = parseTotpFactorsFromListResponse(factorsResult.data)
  const verifiedFactor = factors.find((factor) => factor.status === 'verified') ?? null
  const pendingFactor = factors.find((factor) => factor.status === 'unverified') ?? null
  const currentAal = (aalResult.data?.currentLevel ?? null) as AuthenticatorAssuranceLevel | null
  const nextAal = (aalResult.data?.nextLevel ?? null) as AuthenticatorAssuranceLevel | null

  return {
    factors,
    verifiedFactor,
    pendingFactor,
    hasVerifiedTotp: verifiedFactor !== null,
    currentAal,
    nextAal,
    isSessionElevated: currentAal === 'aal2',
  }
}

export async function enrollSuperadminTotp (): Promise<MfaEnrollResult> {
  const status = await getMfaStatus()
  for (const factor of status.factors.filter((item) => item.status === 'unverified')) {
    await unenrollTotpFactor(factor.id)
  }

  const { data, error } = await supabase.auth.mfa.enroll({
    factorType: 'totp',
    friendlyName: 'Saloni Superadmin',
  })
  if (error) throw error
  if (!data?.id || !data.totp?.qr_code || !data.totp?.secret) {
    throw new Error('Kon TOTP-factor niet aanmaken')
  }

  return {
    factorId: data.id,
    qrCode: data.totp.qr_code,
    secret: data.totp.secret,
  }
}

export async function verifyTotpEnrollment (factorId: string, code: string): Promise<void> {
  const trimmed = normalizeTotpCode(code)

  const { error } = await supabase.auth.mfa.challengeAndVerify({
    factorId,
    code: trimmed,
  })
  if (error) throw error
}

export async function createTotpLoginChallenge (factorId: string): Promise<string> {
  const { data, error } = await supabase.auth.mfa.challenge({ factorId })
  if (error) throw error
  if (!data?.id) throw new Error('Kon MFA-challenge niet starten')
  return data.id
}

export async function verifyTotpLoginChallenge (
  factorId: string,
  challengeId: string,
  code: string
): Promise<void> {
  const trimmed = normalizeTotpCode(code)
  const { error } = await supabase.auth.mfa.verify({
    factorId,
    challengeId,
    code: trimmed,
  })
  if (error) throw error
}

export async function unenrollTotpFactor (factorId: string): Promise<void> {
  const { error } = await supabase.auth.mfa.unenroll({ factorId })
  if (error) throw error
}

export function formatMfaErrorMessage (message: string): string {
  if (/already exists/i.test(message)) {
    return 'Er bestaat al een onvoltooide 2FA-setup. Vernieuw de pagina om verder te gaan, of klik op "Opnieuw beginnen".'
  }
  return message
}

export function getMfaStatusLabel (status: MfaStatus): string {
  if (status.hasVerifiedTotp) {
    if (status.isSessionElevated) return 'Actief (sessie beveiligd)'
    return 'Ingesteld — verifieer bij volgende login'
  }
  if (status.pendingFactor) return 'Setup niet afgerond'
  return 'Niet ingesteld'
}
